import { useEffect, useState } from 'react';

import { PageHeader } from '@/components/layout/PageHeader';
import { EntityLink, NavAction } from '@/components/nav/EntityLink';
import { ProjectBudgetEditor } from '@/components/projects/ProjectBudgetEditor';
import { StateBlock } from '@/components/reports/StateBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldControl, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { matchesQuery, SearchInput } from '@/components/ui/search-input';
import { Select } from '@/components/ui/select';
import { Table, TableWrap, Td, Th } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Toggle } from '@/components/ui/toggle';
import { ApiError, api, type Project, type ProjectType } from '@/lib/api';
import {
  toAssignments,
  toTenantSettings,
  toTimeLogs,
  useQueryParam,
  useSelectedParam,
} from '@/lib/nav';
import { useApi } from '@/lib/useApi';

/**
 * 種別の選択肢。
 *
 * 無効化した種別は選ばせないが、既にその種別が付いているプロジェクトでは
 * 選択肢に残す (開いた途端に別の種別へ化けないようにするため)。
 */
function TypeSelect({
  types,
  value,
  onChange,
  disabled,
}: {
  types: ProjectType[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const options = types.filter((type) => type.status === 'active' || type.id === value);

  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">種別なし</option>
      {options.map((type) => (
        <option key={type.id} value={type.id}>
          {type.name}
          {type.status === 'archived' ? '（無効）' : ''}
        </option>
      ))}
    </Select>
  );
}

/**
 * プロジェクト管理。
 *
 * リポジトリの紐付けもここで行う。MCP から工数を記録するとき、
 * 紐付けがあればプロジェクトの選択自体が不要になる。
 *
 * 種別はテナント設定で管理するマスタなので、選択肢は API から引く。
 */
export function ProjectsPage({ isAdmin }: { isAdmin: boolean }) {
  const { data, error, loading, reload } = useApi<{ projects: Project[] }>('/api/projects');
  const types = useApi<{ projectTypes: ProjectType[] }>('/api/project-types');
  const projectTypes = types.data?.projectTypes ?? [];
  const [creating, setCreating] = useState(false);
  // 開いている行と絞り込みは URL が持つ。他の画面から「このプロジェクト」を
  // 開けるようにするため (?project=<id> でその行が開いた状態で着地する)。
  const [editing, setEditing] = useSelectedParam('project');
  const [query, setQuery] = useQueryParam();

  // 名前だけでなく紐付けたリポジトリと種別でも引けるようにする
  // (「このリポジトリはどのプロジェクトだったか」を調べる用途が実際に多い。
  //  種別はテナント設定の「その種別のプロジェクト」からの着地点でもある)
  const projects = (data?.projects ?? []).filter((project) =>
    matchesQuery(query, project.name, project.projectTypeName, ...project.repositories),
  );

  return (
    <>
      <PageHeader
        title="プロジェクト"
        subtitle={
          data
            ? query.trim() === ''
              ? `${data.projects.length} 件`
              : `${projects.length} / ${data.projects.length} 件`
            : undefined
        }
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setCreating((value) => !value)}>
              {creating ? '閉じる' : '新規作成'}
            </Button>
          ) : null
        }
      />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        {creating ? (
          <CreateForm
            projectTypes={projectTypes}
            onCreated={() => {
              setCreating(false);
              reload();
            }}
          />
        ) : null}

        <StateBlock
          loading={loading}
          error={error}
          empty={data?.projects.length === 0}
          emptyMessage="プロジェクトがありません。"
        >
          <div className="border border-line bg-surface-raised">
            <div className="border-b border-line px-4 py-3">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder="プロジェクト名・種別・リポジトリで絞り込む"
                className="w-full md:w-80"
              />
            </div>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>PROJECT</Th>
                    <Th>TYPE</Th>
                    <Th>CAPITALIZABLE</Th>
                    <Th>REPOSITORIES</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <ProjectRow
                      key={project.id}
                      project={project}
                      projectTypes={projectTypes}
                      isAdmin={isAdmin}
                      expanded={editing === project.id}
                      onToggle={() => setEditing(editing === project.id ? null : project.id)}
                      onSaved={reload}
                    />
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            {projects.length === 0 ? (
              <p className="border-t border-line px-4 py-8 text-sm text-ink-subtle">
                「{query}」に一致するプロジェクトはありません。
              </p>
            ) : null}
          </div>
        </StateBlock>
      </div>
    </>
  );
}

function CreateForm({
  projectTypes,
  onCreated,
}: {
  projectTypes: ProjectType[];
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [projectTypeId, setProjectTypeId] = useState('');
  const [isCapitalizable, setIsCapitalizable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 種別の読み込みは非同期なので、届いた時点で先頭を既定にする
  const firstActive = projectTypes.find((type) => type.status === 'active')?.id ?? '';
  useEffect(() => {
    setProjectTypeId((current) => (current === '' ? firstActive : current));
  }, [firstActive]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/projects', {
        name: name.trim(),
        projectTypeId: projectTypeId || null,
        isCapitalizable,
      });
      onCreated();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
      <h2 className="text-sm font-bold">プロジェクトを作成する</h2>
      <FieldRow>
        <Field label="名前" className="min-w-64 flex-1">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例: 決済基盤リプレイス"
          />
        </Field>
        <Field label="種別">
          <TypeSelect types={projectTypes} value={projectTypeId} onChange={setProjectTypeId} />
        </Field>
        <Field label="資産化対象">
          <FieldControl>
            <Toggle checked={isCapitalizable} onChange={setIsCapitalizable} label="資産化対象" />
          </FieldControl>
        </Field>
        <Button variant="primary" onClick={submit} disabled={busy || name.trim() === ''}>
          作成
        </Button>
      </FieldRow>
      <p className="text-xs text-ink-subtle">
        名前はテナント内で一意です。MCP から名前で引けるようにするための制約です。
      </p>
      {error ? <p className="text-sm text-alert">{error}</p> : null}
    </div>
  );
}

function ProjectRow({
  project,
  projectTypes,
  isAdmin,
  expanded,
  onToggle,
  onSaved,
}: {
  project: Project;
  projectTypes: ProjectType[];
  isAdmin: boolean;
  expanded: boolean;
  onToggle: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(project.name);
  const [projectTypeId, setProjectTypeId] = useState(project.projectTypeId ?? '');
  const [isCapitalizable, setIsCapitalizable] = useState(project.isCapitalizable);
  const [instruction, setInstruction] = useState(project.mcpCustomInstruction ?? '');
  const [repository, setRepository] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, note: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setMessage(note);
      onSaved();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <Td>
          <div className="flex items-center gap-2">
            <span className="font-medium">{project.name}</span>
            {project.status === 'archived' ? <Badge variant="outline">ARCHIVED</Badge> : null}
            {project.budgetMode === 'total' ? <Badge variant="outline">予算 / 通期</Badge> : null}
            {project.budgetMode === 'monthly' ? <Badge variant="outline">予算 / 月次</Badge> : null}
          </div>
        </Td>
        <Td className="text-ink-muted">
          {project.projectTypeName ?? <span className="text-ink-subtle">—</span>}
        </Td>
        <Td>
          {project.isCapitalizable ? (
            <Badge variant="outline">資産化対象</Badge>
          ) : (
            <span className="text-ink-subtle">—</span>
          )}
        </Td>
        <Td>
          {project.repositories.length === 0 ? (
            <span className="text-xs text-ink-subtle">未設定</span>
          ) : (
            <div className="flex flex-col gap-0.5">
              {project.repositories.map((repo) => (
                <span key={repo} className="font-mono text-xs text-ink-secondary">
                  {repo}
                </span>
              ))}
            </div>
          )}
        </Td>
        <Td className="text-right">
          <Button onClick={onToggle} aria-expanded={expanded}>
            {expanded ? '閉じる' : isAdmin ? '編集' : '詳細'}
          </Button>
        </Td>
      </tr>

      {expanded ? (
        <tr>
          <td colSpan={5} className="border-t border-line bg-surface px-6 py-5">
            <div className="grid gap-8 lg:grid-cols-2">
              <section className="flex flex-col gap-4">
                <h3 className="text-sm font-bold">基本情報</h3>
                <Field label="名前">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isAdmin || busy}
                  />
                </Field>
                <Field label="種別">
                  <TypeSelect
                    types={projectTypes}
                    value={projectTypeId}
                    onChange={setProjectTypeId}
                    disabled={!isAdmin || busy}
                  />
                </Field>
                {/* リンクは Field の外に置く。hint は label の中なので、
                    中のリンクを押すと Select まで一緒に開いてしまう */}
                <p className="text-xs text-ink-subtle">
                  種別は<EntityLink target={toTenantSettings()}>テナント設定</EntityLink>
                  で追加・編集できます。
                </p>
                <div className="flex items-center gap-2">
                  <Toggle
                    checked={isCapitalizable}
                    onChange={setIsCapitalizable}
                    label="資産化対象"
                    disabled={!isAdmin || busy}
                  />
                  <span className="text-xs text-ink-secondary">資産化対象</span>
                </div>
                <p className="text-xs leading-relaxed text-ink-subtle">
                  切り替えても既に記録済みの工数は書き換わりません。
                  締めた月の数字が後から動かないようにするためです。
                </p>

                <Field
                  label="MCP の記録ルール"
                  hint="このプロジェクトを選んだ AI に伝わります。例: 「GitHub の Issue 番号を必ずメモに残すこと」"
                >
                  <Textarea
                    rows={3}
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    disabled={!isAdmin || busy}
                  />
                </Field>

                {isAdmin ? (
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      disabled={busy}
                      onClick={() =>
                        run(
                          () =>
                            api.patch(`/api/projects/${project.id}`, {
                              name,
                              projectTypeId: projectTypeId || null,
                              isCapitalizable,
                              mcpCustomInstruction: instruction,
                            }),
                          '保存しました',
                        )
                      }
                    >
                      保存
                    </Button>
                    <Button
                      disabled={busy}
                      onClick={() =>
                        run(
                          () =>
                            api.patch(`/api/projects/${project.id}`, {
                              status: project.status === 'archived' ? 'active' : 'archived',
                            }),
                          project.status === 'archived' ? '再開しました' : 'アーカイブしました',
                        )
                      }
                    >
                      {project.status === 'archived' ? 'アーカイブ解除' : 'アーカイブ'}
                    </Button>
                  </div>
                ) : null}
              </section>

              <section className="flex flex-col gap-4">
                <h3 className="text-sm font-bold">リポジトリの紐付け</h3>
                <p className="text-xs leading-relaxed text-ink-subtle">
                  紐付けておくと、AI クライアントが作業中のリポジトリからこのプロジェクトを
                  自動で選びます。エンジニアに「どのプロジェクトですか」と聞かせずに済みます。 1
                  つのリポジトリは 1 つのプロジェクトにしか紐付きません。
                </p>

                {project.repositories.length === 0 ? (
                  <span className="text-xs text-ink-subtle">まだ紐付いていません。</span>
                ) : (
                  <div className="flex flex-col gap-2">
                    {project.repositories.map((repo) => (
                      <div key={repo} className="flex items-center gap-3">
                        <span className="flex-1 font-mono text-xs text-ink-secondary">{repo}</span>
                        {isAdmin ? (
                          <Button
                            variant="ghost"
                            disabled={busy}
                            onClick={() =>
                              run(
                                () =>
                                  api.deleteWithBody(`/api/projects/${project.id}/repositories`, {
                                    repository: repo,
                                  }),
                                '紐付けを解除しました',
                              )
                            }
                          >
                            解除
                          </Button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}

                {isAdmin ? (
                  <div className="flex gap-2">
                    <Input
                      value={repository}
                      onChange={(e) => setRepository(e.target.value)}
                      placeholder="git@github.com:acme/api.git"
                      className="flex-1 font-mono text-xs"
                      disabled={busy}
                    />
                    <Button
                      disabled={busy || repository.trim() === ''}
                      onClick={() =>
                        run(async () => {
                          await api.post(`/api/projects/${project.id}/repositories`, {
                            repository: repository.trim(),
                          });
                          setRepository('');
                        }, '紐付けました')
                      }
                    >
                      追加
                    </Button>
                  </div>
                ) : null}
                <p className="text-xs text-ink-subtle">
                  <span className="font-mono">git remote -v</span> の URL をそのまま貼れます。
                </p>
              </section>
            </div>

            {/*
              予算は月次モードだと月の数だけ行が並ぶので、左右 2 段の中ではなく
              全幅の 1 段として下に置く。
            */}
            <section className="flex flex-col gap-4 pt-8">
              <h3 className="text-sm font-bold">予算</h3>
              <p className="text-xs leading-relaxed text-ink-subtle">
                金額 (円)
                だけを持ちます。工数の予定はアサイン計画が持っているので、ここでは扱いません。
              </p>
              <ProjectBudgetEditor
                projectId={project.id}
                isAdmin={isAdmin}
                onModeChanged={onSaved}
              />
            </section>

            {/* このプロジェクトの実績と予定へ。名前を覚えて一覧を開き直さなくて済む */}
            <div className="flex flex-wrap gap-2 pt-6">
              <NavAction target={toTimeLogs({ query: project.name })}>工数明細を見る</NavAction>
              <NavAction target={toAssignments({ query: project.name })}>
                アサイン計画を見る
              </NavAction>
            </div>

            {message ? <p className="pt-4 text-xs text-ink-muted">{message}</p> : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}
